import { z } from "zod";
import { withRequiredPresentation } from "@common/schemas/domains/reportSettings/report-presentation.schema";
import { ReportFormat } from "@common/helpers/reports";
import { ClassFilterType } from "@common/types/class";
import {
    CHECK_DETAIL_GROUP_BY_KEYS,
    CheckDetailReconciliationStatus,
} from "@common/types/reports/check-detail";
import { CheckDetailColumn } from "@common/types/views/checkDetailView";
import { AmountRoundingLevel } from "@common/types/transactionRule";

const FILTER_IDS_MAX = 10_000;

const checkDetailReportFilters = z.object({
    entityIds: z.array(z.coerce.number().int().positive()).min(1),
    startDate: z.string().date(),
    endDate: z.string().date(),
    financialAccountIds: z.array(z.coerce.number().int().positive()).optional(),
    reconciliationStatus: z
        .nativeEnum(CheckDetailReconciliationStatus)
        .optional(),
    search: z.string().trim().max(256).optional(),
    counterpartiesIds: z
        .array(z.string().uuid())
        .max(FILTER_IDS_MAX)
        .optional(),
    withEmptyOrUnverifiedCounterparty: z.boolean().optional(),
    hasCounterparty: z.boolean().optional(),
    minAmount: z.coerce.number().optional(),
    maxAmount: z.coerce.number().optional(),
    amountRounded: z.boolean().optional(),
    amountRoundingLevel: z.nativeEnum(AmountRoundingLevel).optional(),
    classes: z.array(z.string().uuid()).max(FILTER_IDS_MAX).optional(),
    classFilterType: z.nativeEnum(ClassFilterType).optional(),
    excludeFrom1099: z.boolean().optional(),
});

const isEndOnOrAfterStart = ({
    startDate,
    endDate,
}: {
    startDate: string;
    endDate: string;
}) => startDate <= endDate;

const END_BEFORE_START_ISSUE = {
    path: ["endDate"],
    message: "End date must be on or after start date",
};

export const checkDetailReportQuery = checkDetailReportFilters.refine(
    isEndOnOrAfterStart,
    END_BEFORE_START_ISSUE,
);

export const checkDetailExportQuery = checkDetailReportFilters
    .extend({
        format: z.nativeEnum(ReportFormat).optional(),
        groupBy: z.enum(CHECK_DETAIL_GROUP_BY_KEYS),
        columns: z.array(z.nativeEnum(CheckDetailColumn)).min(1),
    })
    .refine(isEndOnOrAfterStart, END_BEFORE_START_ISSUE);

export const checkDetailRowSchema = z.object({
    transactionId: z.number().int(),
    entityId: z.number().int(),
    dateOnly: z.string(),
    checkNumber: z.string().nullable(),
    counterpartyId: z.string().nullable(),
    payee: z.string().nullable(),
    description: z.string(),
    memo: z.string().nullable(),
    accountName: z.string(),
    accountCodeText: z.string().nullable(),
    classNames: z.array(z.string()),
    excludeFrom1099: z.boolean(),
    clearedDateOnly: z.string().nullable(),
    journalEntryReference: z.string().nullable(),
    isReconciled: z.boolean(),
    amount: z.number(),
});

export const checkDetailGroupSchema = z.object({
    financialAccountId: z.number().int(),
    financialAccountName: z.string(),
    rows: z.array(checkDetailRowSchema),
    total: z.number(),
});

export const checkDetailResponseSchema = z.object({
    groups: z.array(checkDetailGroupSchema),
    depositGroups: z.array(checkDetailGroupSchema),
});

export const checkDetailResponseWithPresentationSchema =
    withRequiredPresentation(checkDetailResponseSchema);

export type CheckDetailReportQuery = z.infer<typeof checkDetailReportQuery>;
export type CheckDetailExportQuery = z.infer<typeof checkDetailExportQuery>;
export type CheckDetailRow = z.infer<typeof checkDetailRowSchema>;
export type CheckDetailGroup = z.infer<typeof checkDetailGroupSchema>;
export type CheckDetailResponse = z.infer<typeof checkDetailResponseSchema>;
